import { INode } from 'shared/types/tree'
import {
  childNodesOfType,
  isStudentFacing,
  makeStudentFacing,
} from '../../../utility/TreeHelpers'
import { LeafBodyValues, NodeBodyKeys, NodeType } from 'shared/types/ingestion'
import { updateLeafData, updateTreeNode } from '../../TreeRequests'

export function getPresentationLeaf(presentation: INode) {
  const [list] = childNodesOfType(presentation, NodeType.AttachmentsList)
  return list.leaves?.[0]
}

export function getFileId(fileUrl: string) {
  // some assumptions made here
  const [fileId] = fileUrl.split('/').slice(-2)
  return fileId
}

export function resolveSourceFileIssue(url: string, presentation: INode) {
  const presentationLeaf = getPresentationLeaf(presentation)
  if (!presentationLeaf) {
    throw new Error('create this?')
  }
  const fileId = getFileId(url)
  const newBody = { ...presentationLeaf.body }
  if (newBody.file_id === fileId) {
    delete newBody.source_file
  } else {
    newBody.file_id = fileId
    newBody.file = newBody.source_file
    delete newBody.source_file
  }
  return updateLeafData({
    tree_id: presentation.tree_id,
    version: presentation.version,
    id: presentationLeaf.id,
    body: newBody,
  })
}

function presentationBodyHasIssues(presentation: INode) {
  if (!presentation.body) return true
  if (!isStudentFacing(presentation)) return true
  if (!presentation.body[NodeBodyKeys.Assignable]) return true
}

// Ingested presentation is in an invalid state if it is
// not marked as student facing
// not marked as assignable
// attachments list is not student facing
// attachments list has leaves that are not marked as is_ingested
// notes should be student facing
export function presentationHasIssues(presentation: INode) {
  if (presentationBodyHasIssues(presentation)) return true
  const attachmentsLists = childNodesOfType(
    presentation,
    NodeType.AttachmentsList,
  )
  if (
    attachmentsLists.find((list) => {
      if (!isStudentFacing(list)) return true
      return list.leaves?.find((leaf) => !leaf.body[LeafBodyValues.IsIngested])
    })
  ) {
    return true
  }
}

// At this point we know the presentation has issues and we want to resolve them
// We don't know what issues the presentation has, it might have multiple issues
// so we need to identify what issues it has and make requests to fix each issue
// TODO: push this to the BE
export function fixAssignablePresentation(presentation: INode) {
  const requests: Promise<unknown>[] = []
  const treeParams = {
    tree_id: presentation.tree_id,
    version: presentation.version,
  }
  if (presentationBodyHasIssues(presentation)) {
    const body = {
      ...presentation.body,
      [NodeBodyKeys.StudentFacing]: true,
      [NodeBodyKeys.Assignable]: true,
    }
    const params = {
      ...treeParams,
      id: presentation.id,
      body,
    }
    requests.push(updateTreeNode(params).then(() => (presentation.body = body)))
  }
  childNodesOfType(presentation, NodeType.AttachmentsList).forEach((list) => {
    if (!list.body?.[NodeBodyKeys.StudentFacing]) {
      requests.push(makeStudentFacing(list))
    }
    list.leaves?.forEach((leaf) => {
      if (!leaf.body[LeafBodyValues.IsIngested]) {
        const body = {
          ...leaf.body,
          [LeafBodyValues.IsIngested]: true,
        }
        const params = {
          ...treeParams,
          id: leaf.id,
          body,
        }
        requests.push(updateLeafData(params).then(() => (leaf.body = body)))
      }
    })
    childNodesOfType(presentation, NodeType.Note).forEach((note) => {
      if (!isStudentFacing(note)) {
        requests.push(makeStudentFacing(note))
      }
    })
  })

  return Promise.all(requests)
}
